import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { COMPANY, NAV_LINKS, SERVICES } from "@/lib/site-data";

export default function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-300 grid-bg-dark" data-testid="site-footer">
      {/* Big CTA */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 lg:pt-28 pb-14">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-400 mb-4">
              Let's talk
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05]">
              Ready to keep your <br />
              infrastructure <span className="text-red-500">running 24/7?</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="footer-quote-cta"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3.5 rounded-md font-semibold transition-all hover:-translate-y-0.5"
            >
              Get a Quote <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="footer-call-cta"
              className="inline-flex items-center gap-2 border-2 border-slate-700 hover:border-white text-white px-6 py-3.5 rounded-md font-semibold transition-all"
            >
              Call Us
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800/80">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-10">
          <div className="col-span-2 lg:col-span-4">
            <Link to="/" className="flex items-center gap-3" data-testid="footer-logo-link">
              <div className="h-11 w-11 rounded-md bg-white p-1">
                <img src={COMPANY.logo} alt="R-Tech" className="h-full w-full object-contain" />
              </div>
              <div>
                <div className="font-display text-xl font-black text-white">
                  R-Tech <span className="text-red-500">Computer</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  {COMPANY.tagline}
                </div>
              </div>
            </Link>
            <p className="mt-6 text-sm leading-relaxed text-slate-400 max-w-sm">
              Reliable 24/7 IT support and skilled technical manpower keeping enterprise
              infrastructure running across India.
            </p>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-5">
              Company
            </h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.path}>
                  <Link
                    to={l.path}
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-5">
              Services
            </h4>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/services"
                    className="text-sm text-slate-400 hover:text-white transition-colors"
                    data-testid={`footer-service-${s.slug}`}
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-2 lg:col-span-3">
            <h4 className="text-white text-sm font-bold uppercase tracking-widest mb-5">
              Contact
            </h4>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-blue-400 flex-shrink-0" />
                <a href={`tel:${COMPANY.phoneRaw}`} className="text-slate-300 hover:text-white" data-testid="footer-phone">
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-blue-400 flex-shrink-0" />
                <a href={`mailto:${COMPANY.email}`} className="text-slate-300 hover:text-white break-all" data-testid="footer-email">
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-blue-400 flex-shrink-0" />
                <span className="text-slate-400 leading-relaxed">{COMPANY.address}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800/80">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col md:flex-row justify-between items-center gap-2 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} R-Tech Computer. All rights reserved.</p>
          <p className="font-mono">Reliable 24/7 IT Support · Pan-India</p>
        </div>
      </div>
    </footer>
  );
}
